import * as React from 'react';
import classNames from 'classnames';
import { MediaSet } from '@/types/media';
import { Image } from '../Image';
import { TextBadge } from '../TextBadge';
import styles from './ArticleHeader.module.scss';

/** The design shows one label, and the article cards cap theirs at three */
const maxLabels = 3;

export interface ArticleHeaderProps {
	readonly className?: string;
	/** The categories the article is filed under, shown above the headline in the given order */
	readonly categories?: readonly string[];
	readonly headline: string;
	readonly image?: MediaSet;
	readonly altText?: string;
	/** Already formatted for the current culture */
	readonly publicationDate?: string;
	/** Free text from the CMS, e.g. "3 min" */
	readonly readingTime?: string;
	/** The article's own tags, shown under the meta line */
	readonly labels?: readonly string[];
	/** Heads the tag list, e.g. "Related tags:". The list stays hidden without it */
	readonly relatedTagsLabel?: string;
}

/**
 * Generic ArticleHeader component, the top of an article: what it is filed under, what it
 * is called, its image, when it was written, and the tags it carries.
 *
 * The image runs the full width while the text stays in the narrow reading column, as
 * the design has it.
 */
export const ArticleHeader: React.FunctionComponent<ArticleHeaderProps> = ({
	className,
	categories,
	headline,
	image,
	altText,
	publicationDate,
	readingTime,
	labels,
	relatedTagsLabel,
}) => {
	// Skipping the empty ones keeps a missing date or reading time from leaving a stray separator
	const metaText = [publicationDate, readingTime].filter(Boolean).join(' | ');
	const visibleLabels = labels?.slice(0, maxLabels) ?? [];
	// Without the heading the pills read as a stray row, so the whole section waits for it
	const showTags = visibleLabels.length > 0 && !!relatedTagsLabel;

	return (
		<header className={className}>
			<div className={classNames(styles.heading, 'u-container-sm')}>
				{categories && categories.length > 0 && (
					<ul className={styles.pills}>
						{categories.map((category) => (
							<li key={category}>
								<TextBadge className={styles.pill} text={category} />
							</li>
						))}
					</ul>
				)}
				<h1 className={styles.headline}>{headline}</h1>
			</div>
			{image && (
				<div className={classNames(styles.imageSection, 'u-container-lg')}>
					<div className={styles.imageWrapper}>
						<Image
							className={styles.image}
							{...image}
							responsiveSizesSettings={'100vw'}
							altText={image.altText || altText || ''}
						/>
					</div>
				</div>
			)}
			<div className={'u-container-sm'}>
				{metaText && <p className={styles.meta}>{metaText}</p>}
				{showTags && (
					<div className={styles.tags}>
						<p className={styles.tagsLabel} id={'articleHeaderTags'}>
							{relatedTagsLabel}
						</p>
						<ul className={styles.pills} aria-labelledby={'articleHeaderTags'}>
							{visibleLabels.map((label) => (
								<li key={label}>
									<TextBadge className={styles.pill} text={label} />
								</li>
							))}
						</ul>
					</div>
				)}
			</div>
		</header>
	);
};
